import { Component, Input, SimpleChanges } from '@angular/core';
import { MemeTokenProfile } from '@src/app/modules/meme-dashboard/meme-dashboard.type';
import { initialOption, volumeChartInitialOption } from '../../token-detail.data';
import { EchartsOption } from '@shared/directives/echarts.directive';
import { getHandleTimer } from '@src/app/modules/meme-dashboard/meme-dashboard.util';
import { RequestService } from '@src/app/services/request.service';
import { ChainEnum } from '@src/app/shared/types/wallet-chain.type';
import { formatNumberWithPrefix } from '@src/app/shared/utils/number.util';
import { isNumber } from 'lodash';
import { CommonService } from '@src/app/services/common.service';

@Component({
    selector: 'app-v2-token-info',
    templateUrl: './v2-token-info.component.html',
    styleUrls: ['./v2-token-info.component.less'],
})
export class V2TokenInfoComponent {
    @Input() tokenInfo: MemeTokenProfile;
    @Input() chain: ChainEnum;
    @Input() tokenAddress: string;

    formatNumberWithPrefix = formatNumberWithPrefix;

    volumeAnalysisCollapsed = false;
    memeInfoCollapsed = false;
    initialOption = initialOption;
    optionsToBeUpdate: EchartsOption;
    trendingChartLoading = true;

    volumeChartInitialOption = volumeChartInitialOption;
    volumeOptionsToBeUpdate: EchartsOption;
    hasImageDevStatsTokens: MemeTokenProfile['dev_stats']['tokens_deployed'];
    lastSmartBuyAge: string;
    volumeAnalysis: {
        insider_volume_24h: number;
        retail_volume_24h: number;
        insider_net_volume_5m: number;
        insider_net_volume_15m: number;
        insider_net_volume_1h: number;
        insider_net_volume_24h: number;
        retail_net_volume_5m: number;
        retail_net_volume_15m: number;
        retail_net_volume_1h: number;
        retail_net_volume_24h: number;
    };
    volumeChartData: [string, number, number][]; // [date, insider_volume, retail_volume]

    get insiderPercent() {
        const total = (this.volumeAnalysis?.insider_volume_24h || 0) + (this.volumeAnalysis?.retail_volume_24h || 0);
        return total ? (this.volumeAnalysis?.insider_volume_24h || 0) / total : 0;
    }
    get retailPercent() {
        const total = (this.volumeAnalysis?.insider_volume_24h || 0) + (this.volumeAnalysis?.retail_volume_24h || 0);
        return total ? (this.volumeAnalysis?.retail_volume_24h || 0) / total : 0;
    }

    constructor(private requestService: RequestService, private commonService: CommonService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['tokenInfo'].currentValue) {
            this.trendingChartLoading = false;
            this.setupChart(this.tokenInfo?.price_ohlc?.map(item => [item[0], item[4]]) || []);
            this.setDevStatsTokens();
            this.lastSmartBuyAge = this.tokenInfo?.smart_money_last_trade_block_timestamp
                ? getHandleTimer(this.tokenInfo?.smart_money_last_trade_block_timestamp)
                : '-';
        }
    }

    ngOnInit() {
        this.requestService.get<{ data: any[] }>(`/v1/socialscan/meme-dashboard/token/${this.chain}/${this.tokenAddress}/volume-chart`).subscribe(res => {
            this.volumeChartData = res.data.slice(-48) as [string, number, number][];
            this.setupVolumeChart(this.volumeChartData);
        });

        // /v1/socialscan/meme-dashboard/token/sol/3BoAAmetjfwyKS7ga7phjA1BVSkjSawFk7ziDw1DLq5K/volume-analysis
        this.requestService.get<any>(`/v1/socialscan/meme-dashboard/token/${this.chain}/${this.tokenAddress}/volume-analysis`).subscribe(res => {
            this.volumeAnalysis = res;
        });
    }

    setupChart(data: [string, number][]) {
        if (data.length === 0) {
            return;
        }

        const max = Math.max(...data.map(item => item[1]));
        const min = Math.min(...data.map(item => item[1]));
        const lastIndex = data.length - 1;
        const lastValue = data[lastIndex][1];

        this.optionsToBeUpdate = {
            grid: {
                top: 4,
                right: 4,
                bottom: 4,
                left: 4,
            },
            xAxis: {
                type: 'category',
                show: false,
                boundaryGap: false,
            },
            yAxis: {
                type: 'value',
                show: false,
                min,
                max,
                scale: false,
            },
            series: [
                {
                    type: 'line',
                    lineStyle: {
                        width: 2,
                    },
                    smooth: false,
                    symbolSize: 0,
                    data,
                    color: '#797979',
                    markPoint: {
                        symbol: 'circle',
                        symbolSize: 5,
                        itemStyle: {
                            color: '#fff',
                            borderWidth: 2,
                            borderColor: '#797979',
                        },
                        data: [
                            {
                                coord: [lastIndex, lastValue],
                                name: 'last',
                            },
                        ],
                    },
                },
            ],
        };
    }

    setupVolumeChart(data: [string, number, number][]) {
        this.volumeOptionsToBeUpdate = {
            xAxis: {
                data: data.map(item => item[0]),
            },
            grid: {
                left: 20,
                right: 25,
                top: 30,
                bottom: this.commonService.isMobile ? 60 : 40,
                containLabel: true,
            },
            legend: {
                data: [
                    {
                        name: 'Retail Trading Volume Participation Rate %',
                        icon: 'path://M0 0 H18 V2 H0 Z',
                        itemStyle: {
                            color: '#CBCBCB',
                        },
                    },
                    { name: 'Insider Trading Volume' },
                    { name: 'Retail Trading Volume' },
                ],
                bottom: this.commonService.isMobile ? 4 : 10,
                itemGap: this.commonService.isMobile ? 12 : 20,
                itemHeight: 10,
                left: this.commonService.isMobile ? 0 : 'center',
                itemWidth: 10,
                textStyle: { fontSize: 9 },
            },
            series: [
                {
                    name: 'Insider Trading Volume',
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: '#9EAAFF' },
                    emphasis: { focus: 'self' },
                    blur: { itemStyle: { opacity: 0.2 } },
                    data: data.map(item => item[1]),
                    yAxisIndex: 0,
                },
                {
                    name: 'Retail Trading Volume',
                    type: 'bar',
                    stack: 'total',
                    itemStyle: { color: '#FFCC49' },
                    emphasis: { focus: 'self' },
                    blur: { itemStyle: { opacity: 0.2 } },
                    data: data.map(item => item[2]),
                    yAxisIndex: 0,
                },
                {
                    name: 'Retail Trading Volume Participation Rate %',
                    type: 'line',
                    yAxisIndex: 1,
                    symbol: 'circle',
                    showSymbol: false,
                    color: 'transparent',
                    emphasis: {
                        focus: 'series',
                        itemStyle: {
                            color: '#fff',
                            borderColor: '#797979',
                            borderWidth: 3,
                        },
                        scale: 1.5,
                    },
                    blur: {
                        itemStyle: {
                            opacity: 0,
                        },
                    },
                    lineStyle: { color: '#CBCBCB', width: 2 },
                    data: data.map(item => {
                        const value = Number(((item[2] / (item[1] + item[2])) * 100).toFixed(4));
                        return isNaN(value) ? 0 : value;
                    }),
                    smooth: true,
                },
            ],
        };
    }

    localFormatNumber(value: number) {
        return isNumber(value) ? formatNumberWithPrefix(value, '$') : '-';
    }

    private setDevStatsTokens() {
        const tokensWithLogo = this.tokenInfo?.dev_stats?.tokens_deployed?.filter(item => item.logo) || [];
        const tokensWithoutLogo = this.tokenInfo?.dev_stats?.tokens_deployed?.filter(item => !item.logo) || [];

        if (tokensWithLogo.length >= 6) {
            this.hasImageDevStatsTokens = tokensWithLogo.slice(0, 6);
        } else {
            const remainingCount = 6 - tokensWithLogo.length;
            this.hasImageDevStatsTokens = [...tokensWithLogo, ...tokensWithoutLogo.slice(0, remainingCount)];
        }
    }
}
